import { Component, OnInit, OnDestroy } from '@angular/core';
import { CtrlNavigationEntry, Breadcrumb } from '../../shared/_models/models';
import { Common } from '../../_common';
import { LayoutService } from '../_services/layout.service';
import { ActivatedRoute, NavigationEnd, PRIMARY_OUTLET, Router, UrlSegment } from '@angular/router';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './app-breadcrumbs.component.html',
  styleUrls: ['./app-breadcrumbs.component.less'],
})
export class AppBreadcrumbsComponent implements OnInit, OnDestroy {

  navItems: CtrlNavigationEntry[] = [];
  breadcrumbs: Breadcrumb[] = [];
  breadcrumbsFromLocalStorage: Breadcrumb[] = [];
  allBreadcrumbOptions: { [navId: string]: { [path: string]: string } } = {};
  private unsubscribe$ = new Subject<void>();

  constructor(
    private common: Common,
    private layoutService: LayoutService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.initServiceSubscriptions();
    this.initRouterEvents();
  }

  initServiceSubscriptions() {
    this.layoutService.currentContext$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.buildBreadcrumbs();
    });
  }

  initRouterEvents() {
    this.router.events
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(event => event instanceof NavigationEnd),
        tap(() => {
          this.buildBreadcrumbs();
        })
      )
      .subscribe();
  }

  buildBreadcrumbs() {
    this.getLocalStorage();
    this.buildBreadcrumbsFromRoute([], this.route.root);
    this.setLocalStorage();
  }

  buildBreadcrumbsFromRoute(
    breadcrumbs: Breadcrumb[],
    route: ActivatedRoute,
    accumulatedPathSegments: string[] = [],
    pendingPathSegments: string[] = [],
    pendingLabelSegments: string[] = []
  ): void {
    const children: ActivatedRoute[] = route.children;
    if (children.length) {
      for (const child of children) {
        if (child.outlet !== PRIMARY_OUTLET) {
          continue;
        }
        const routePathSegments: string[] = child.snapshot.url.map((segment: UrlSegment) => segment.path).filter(Boolean);
        const routeBreadcrumbLabels: string[] = this.getRouteBreadcrumbLabels(child);
        if (routeBreadcrumbLabels.length && routePathSegments.length) {
          const allLabels = [...pendingLabelSegments, ...routeBreadcrumbLabels];
          accumulatedPathSegments = [...accumulatedPathSegments, ...pendingPathSegments, ...routePathSegments];
          this.pushToBreadcrumbs(breadcrumbs, allLabels, accumulatedPathSegments);
          pendingPathSegments = [];
          pendingLabelSegments = [];
        } else {
          pendingPathSegments = [...pendingPathSegments, ...routePathSegments];
          pendingLabelSegments = [...pendingLabelSegments, ...routeBreadcrumbLabels];
        }
        this.buildBreadcrumbsFromRoute(breadcrumbs, child, accumulatedPathSegments, pendingPathSegments, pendingLabelSegments);
      }
    } else {
      if (pendingLabelSegments.length > 0) {
        this.pushToBreadcrumbs(breadcrumbs, pendingLabelSegments, pendingPathSegments);
        pendingLabelSegments = [];
        pendingPathSegments = [];
      }
      this.breadcrumbs = breadcrumbs;
    }
  }

  pushToBreadcrumbs(breadcrumbs: Breadcrumb[], labels: string[], pathSegments: string[]) {
    labels.forEach((label, index) => {
      if (index === labels.length - 1) {
        // Last label gets the full URL with all pending segments
        breadcrumbs.push({
          label,
          url: pathSegments.join('/'),
          queryParams: this.findQueryParamsFromLocalStorageBackup(pathSegments.join('/'))
        });
      } else {
        // Earlier labels get the current URL without pending segments
        breadcrumbs.push({ label, url: null, queryParams: null });
      }
    });
  }

  getRouteBreadcrumbLabels(route: ActivatedRoute): string[] {
    const routeData = route.routeConfig?.data;
    let labels: string[] = [];
    if (routeData?.breadcrumb) {
      if (typeof routeData.breadcrumb === 'function') {
        const result = routeData.breadcrumb(route.snapshot.data);
        labels = Array.isArray(result) ? result : [result];
      } else {
        labels = Array.isArray(routeData.breadcrumb) ? routeData.breadcrumb : [routeData.breadcrumb];
      }
    }
    return labels;
  }

  getLocalStorage() {
    this.breadcrumbsFromLocalStorage = <Breadcrumb[]>JSON.parse(localStorage.getItem('crumbsParams'));
  }

  setLocalStorage() {
    if (this.breadcrumbsFromLocalStorage) {
      this.breadcrumbs.forEach((crumb, idx) => {
        if (crumb.url === this.breadcrumbsFromLocalStorage[idx]?.url) {
          crumb = { ...this.breadcrumbsFromLocalStorage[idx] };
        }
      });
    }
    if (location.search && this.breadcrumbs.length) {
      this.breadcrumbs[this.breadcrumbs.length - 1].queryParams = location.search;
    }
    localStorage.setItem('crumbsParams', JSON.stringify(this.breadcrumbs));
  }

  findQueryParamsFromLocalStorageBackup(path: string): string {
    return this.breadcrumbsFromLocalStorage?.filter(crumb => path === crumb.url)[0]?.queryParams || null;
  }

  getQueryParamsObject(query: string) {
    return this.common.toQueryParamsObject(query);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
